import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/spinner";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const PURPLE = "#c57eff";
const CYAN = "#81ecff";

const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
    <path d="M10 7H4M4 7l3-3M4 7l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function FanDetail() {
  const { id } = useParams<{ id: string }>();
  const { active } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ["fan", active?.slug, id],
    queryFn: () => endpoints.getFan(active!.slug, id!),
    enabled: !!active && !!id,
  });

  if (isLoading) return <div className="p-6"><Spinner /></div>;
  if (error) return <div className="p-6"><p className="text-sm text-destructive">{(error as Error).message}</p></div>;
  if (!data) return null;

  const f = data.fan;

  return (
    <div className="p-6 flex flex-col gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>{f.email ?? "Anonymous fan"}</CardTitle>
              <CardDescription>
                {[f.country, f.source].filter(Boolean).join(" · ") || "—"} · first seen {f.first_seen?.slice(0, 10)} · last seen {f.last_seen?.slice(0, 10)}
              </CardDescription>
              {data.firstTouchDj && (
                <div className="mt-3">
                  <Badge variant="purple">first-touch DJ · {data.firstTouchDj.name}</Badge>
                </div>
              )}
            </div>
            <Link to="/app/fans">
              <Button variant="ghost" size="sm"><ArrowLeft /> <span className="ml-2">Back to Fans</span></Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      {data.byRelease.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Releases touched</CardTitle>
            <CardDescription>Per-release engagement for this fan.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Release</TableHead>
                  <TableHead>Artist</TableHead>
                  <TableHead>Engagement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.byRelease.map((r) => (
                  <TableRow key={r.release.slug}>
                    <TableCell className="font-normal">
                      <Link to={`/app/releases/${r.release.slug}`} className="hover:underline">{r.release.title}</Link>
                    </TableCell>
                    <TableCell><span style={{ color: "#adaaaa" }}>{r.release.artist || "—"}</span></TableCell>
                    <TableCell className="text-xs">
                      {Object.entries(r.counts).map(([k, v]) => `${k}: ${v}`).join("  ·  ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
          <CardDescription>
            {data.timeline.length === 0
              ? "No events linked yet."
              : `${data.timeline.length} events tied to this fan.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.timeline.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Events attach on opt-in (retroactively, by fingerprint) and on every subsequent visit.
            </p>
          ) : (
            <div className="space-y-2">
              {data.timeline.map((e) => (
                <div key={e.id} className="rounded-md border border-border px-3 py-2.5 flex items-center justify-between gap-3" style={{ backgroundColor: "#1e1e1e" }}>
                  <div className="text-sm">
                    <span style={{ backgroundColor: "#262626", color: CYAN, padding: "2px 8px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 400 }} className="uppercase tracking-wider mr-2">{e.type}</span>
                    {e.release && (
                      <Link to={`/app/releases/${e.release.slug}`} className="hover:underline">{e.release.title}</Link>
                    )}
                    {e.destination && <span style={{ color: "#adaaaa" }}> → {e.destination}</span>}
                    {e.via_dj && (
                      <span style={{ color: PURPLE }} className="ml-2 text-xs">via {e.via_dj}</span>
                    )}
                  </div>
                  <span className="text-xs" style={{ color: "#adaaaa" }}>{e.ts?.slice(0, 16)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
